import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../apis/store";
import Navbar from "./Navbar";
import Footer from "../pages/Sections/Footer";
import { ToSearch } from "../../apis/actions/search.actions";
import { Card, Row, Col, Spin } from "antd";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { getServiceCatalogPrice } from "../../utils/serviceCatalog";
import { isValidServiceImage } from "../../utils/serviceImage";

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { searchList, loading } = useSelector(
    (state: RootState) => state.search
  );
  const { t , i18n } = useTranslation();
  const location = useLocation();
  const { name } = location.state || {};

  useEffect(() => {
    dispatch(ToSearch({ name: name, lang: i18n.language })); 
  }, [dispatch, name]);

  const {
    products = [],
    services = [],
    offers = [],
    news = [],
  } = searchList?.list?.result || {};
  const isLoading = searchList.status === "idle";
  const noResults =
    !isLoading &&
    products.length === 0 &&
    services.length === 0 &&
    offers.length === 0 &&
    news.length === 0;

  return (
    <>
      <Navbar type="internal" />
      <Row gutter={[16, 16]} justify="center">
        <Col span={20} style={{ marginTop: "2rem" }}>
          <h3>
            {t("search.result")} "{name}"
          </h3>
        </Col>
      </Row>

      {/* Loader */}
      {isLoading && (
        <Row justify="center" style={{ marginTop: "2rem" }}>
          <Spin className="custom-spin" size="large" />{" "}
        </Row>
      )}

      {/* No Results Message */}
      {noResults && (
        <Row justify="center" style={{ marginTop: "2rem" , color:"#999" }}>
          <h5>{t("search.no_results")}</h5>
        </Row>
      )}
      <div className="search-arabic-font ac--font-expo" style={{ marginTop: "2rem" }}>
        <Row gutter={[16, 16]} justify={"center"}>
          {products?.map((product: any) => (
            <Col span={20} key={product.id} style={{ marginTop: "2rem" }}>
              <Card
                hoverable
                actions={[
                  <div
                    className="card-price"
                    style={{ textAlign: "end", marginLeft: "1rem" }}
                  >
                    <button
                      className="btn  btn-gray rounded-3 gap-2 button-add-to-cart"
                      onClick={() =>
                        navigate("/detail", {
                          state: {
                            id: product.id,
                            type: "product",
                          },
                        })
                      }
                    >
                      {t("profile.order.details")}
                    </button>
                  </div>,
                ]}
              >
                <Row>
                  {/* Image Column */}
                  <Col span={8}>
                    {product.image_1920 ? (
                      <img
                        className="search--image-card"
                        alt={product.name}
                        src={product.image_1920}
                      />
                    ) : (
                      <div className="search-no-image-div">
                        <span>{t("search.no_image")}</span>
                      </div>
                    )}
                  </Col>

                  {/* Content Column */}
                  <Col span={16} style={{ paddingRight: "16px" }}>
                    <p style={{ color: "#999" }}>{t("menu.products")}</p>

                    <h3 style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {product.name}
                    </h3>
                    <p style={{ color: "#999" }}>{product.categ_id}</p>
                    <h4 style={{ marginBottom: 8, color: "rgb(155, 74, 127)" }} className="icon-saudi_riyal">
                      {product.lst_price.toFixed(2)} 
                      {/*{t("profile.currency")}*/}
                    </h4>
                    <p style={{ color: "#999" }}>
                      {product.description && parse(product.description)}
                    </p>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 16]} justify={"center"}>
          {services?.map((service: any) => (
            <Col span={20} key={service.id} style={{ marginTop: "2rem" }}>
              <Card
                hoverable
                actions={[
                  <div
                    className="card-price"
                    style={{ textAlign: "end", marginLeft: "1rem" }}
                  >
                    <button
                      className="btn  btn-gray rounded-3 gap-2 button-add-to-cart"
                      onClick={() =>
                        navigate("/detail", {
                          state: {
                            id: service.id,
                            type: "service",
                          },
                        })
                      }
                    >
                      {t("profile.order.details")}
                    </button>
                  </div>,
                ]}
              >
                <Row>
                  {/* Image Column */}
                  <Col span={8}>
                    {isValidServiceImage(service.image_1920) ? (
                      <img
                        alt={service.name}
                        src={service.image_1920}
                        className="search--image-card"
                      />
                    ) : (
                      <div className="search-no-image-div">
                        <span>{t("search.no_image")}</span>
                      </div>
                    )}
                  </Col>

                  {/* Content Column */}
                  <Col span={16} style={{ paddingRight: "16px" }}>
                    <p style={{ color: "#999" }}> {t("menu.services")}</p>

                    <h3 style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {service.name}
                    </h3>
                    <p style={{ color: "#999" }}>{service.categ_id}</p>
                    <h4 style={{ marginBottom: 8, color: "rgb(155, 74, 127)" }} className=" icon-saudi_riyal"> 
                      {getServiceCatalogPrice(service).toFixed(2)} 
                      {/*{t("profile.currency")}*/}
                    </h4>
                    <p style={{ color: "#999" }}>
                      {service.description && parse(service.description)}
                    </p>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 16]} justify={"center"}>
          {offers?.map((offer: any) => (
            <Col span={20} key={offer.id} style={{ marginTop: "2rem" }}>
              <Card
                hoverable
                actions={[
                  <div
                    className="card-price"
                    style={{ textAlign: "end", marginLeft: "1rem" }}
                  >
                    <button
                      className="btn  btn-gray rounded-3 gap-2 button-add-to-cart"
                      onClick={() =>
                        navigate(`/offer-detail`, {
                          state: {
                            id: offer.id,
                            name: offer.name,
                            summary: offer.summary,
                            details: offer.details,
                            image: offer.image_1920,
                          },
                        })
                      }
                    >
                      {t("profile.order.details")}
                    </button>
                  </div>,
                ]}
              >
                <Row>
                  {/* Image Column */}
                  <Col span={8}>
                    {offer.image_1920 ? (
                      <img
                        alt={offer.name}
                        src={offer.image_1920}
                        className="search--image-card"
                      />
                    ) : (
                      <div className="search-no-image-div">
                        <span>{t("search.no_image")}</span>
                      </div>
                    )}
                  </Col>

                  {/* Content Column */}
                  <Col span={16} style={{ paddingRight: "16px" }}>
                    <p style={{ color: "#999" }}>{t("menu.offers")}</p>

                    <h3 style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {offer.name}
                    </h3>
                    <p style={{ color: "#999" }}>{offer.summary}</p>

                    <p style={{ color: "#999" }}>
                      {offer.details && parse(offer.details)}
                    </p>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} justify={"center"}>
          {news?.map((newItem: any) => (
            <Col span={20} key={newItem.id} style={{ marginTop: "2rem" }}>
              <Card
                hoverable
                actions={[
                  <div
                    className="card-price"
                    style={{ textAlign: "end", marginLeft: "1rem" }}
                  >
                    <button
                      className="btn  btn-gray rounded-3 gap-2 button-add-to-cart"
                      onClick={() =>
                        navigate(`/news-detail`, {
                          state: {
                            id: newItem.id,
                            name: newItem.name,
                            description: newItem.summary,
                            details: newItem.details,
                            image: newItem.image_1920,
                            date: newItem.date,
                            tags_ids: newItem.tag_ids,
                          },
                        })
                      }
                    >
                      {t("profile.order.details")}
                    </button>
                  </div>,
                ]}
              >
                <Row>
                  {/* Image Column */}
                  <Col span={8}>
                    {newItem.image_1920 ? (
                      <img
                        alt={newItem.name}
                        src={newItem.image_1920}
                        className="search--image-card"
                      />
                    ) : (
                      <div className="search-no-image-div">
                        <span>{t("search.no_image")}</span>
                      </div>
                    )}
                  </Col>

                  {/* Content Column */}
                  <Col span={16} style={{ paddingRight: "16px" }}>
                    <p style={{ color: "#999" }}>{t("menu.news")}</p>

                    <h3 style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {newItem.name}
                    </h3>
                    <p style={{ color: "#999" }}>{newItem.summary}</p>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      <Footer type="external" />
    </>
  );
};

export default SearchPage;
